import React, { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { 
  Car, Plus, Trash2, Edit, Search, RefreshCw, Database, 
  ChevronRight, Loader2, Zap, Fuel, Settings, Gauge,
  TrendingUp, ChevronDown
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Fuel type labels
const FUEL_LABELS = {
  petrol: 'Benzin',
  diesel: 'Diesel',
  hybrid: 'Hybrid',
  electric: 'Elektro',
  plugin_hybrid: 'Plug-in Hybrid'
};

// Aspiration labels
const ASPIRATION_LABELS = {
  turbo: 'Turbo',
  supercharged: 'Kompressor',
  naturally_aspirated: 'Sauger',
  electric: 'Elektrisch'
};

export const AdminVehicleCatalog = () => {
  // Stats
  const [stats, setStats] = useState({ total_brands: 0, total_models: 0, total_engines: 0, engines_with_tuning: 0 });
  const [loading, setLoading] = useState(true);
  
  // Data
  const [brands, setBrands] = useState([]);
  const [selectedBrand, setSelectedBrand] = useState(null);
  const [models, setModels] = useState([]);
  const [selectedModel, setSelectedModel] = useState(null);
  const [engines, setEngines] = useState([]);
  const [selectedEngine, setSelectedEngine] = useState(null);
  
  // Loading states
  const [loadingModels, setLoadingModels] = useState(false);
  const [loadingEngines, setLoadingEngines] = useState(false);
  const [populating, setPopulating] = useState(false);
  const [enhancingEngines, setEnhancingEngines] = useState(false);
  const [populatingBrand, setPopulatingBrand] = useState(false);
  
  // Search
  const [brandSearch, setBrandSearch] = useState('');
  const [modelSearch, setModelSearch] = useState('');
  const [engineSearch, setEngineSearch] = useState('');
  
  // Dialogs
  const [showAddBrand, setShowAddBrand] = useState(false);
  const [showAddModel, setShowAddModel] = useState(false);
  const [showAddEngine, setShowAddEngine] = useState(false);
  const [showEngineDetails, setShowEngineDetails] = useState(false);
  const [newItem, setNewItem] = useState('');
  const [newEngineData, setNewEngineData] = useState({
    name: '', engine_code: '', displacement_cc: '', fuel_type: 'petrol',
    power_ps: '', power_kw: '', torque_nm: '', aspiration: 'turbo', drivetrain: 'FWD'
  });

  useEffect(() => {
    fetchStats();
    fetchBrands();
  }, []);

  const getToken = () => localStorage.getItem('token');
  
  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/vehicle-catalog/stats`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  const fetchBrands = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/vehicle-catalog/brands`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      setBrands(response.data.brands || []);
    } catch (error) {
      toast.error('Fehler beim Laden der Marken');
    } finally {
      setLoading(false);
    }
  };

  const fetchModels = async (brand) => {
    setLoadingModels(true);
    setModels([]);
    setEngines([]);
    setSelectedModel(null);
    setSelectedEngine(null);
    try {
      const response = await axios.get(`${API}/vehicle-catalog/brands/${encodeURIComponent(brand)}/models`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      setModels(response.data.models || []);
    } catch (error) {
      toast.error('Fehler beim Laden der Modelle');
    } finally {
      setLoadingModels(false);
    }
  };

  const fetchEngines = async (brand, model) => {
    setLoadingEngines(true);
    setEngines([]);
    setSelectedEngine(null);
    try {
      const response = await axios.get(
        `${API}/vehicle-catalog/brands/${encodeURIComponent(brand)}/models/${encodeURIComponent(model)}/engines`,
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      setEngines(response.data.engines || []);
    } catch (error) {
      toast.error('Fehler beim Laden der Motoren');
    } finally {
      setLoadingEngines(false);
    }
  };

  const handleSelectBrand = (brand) => {
    setSelectedBrand(brand);
    fetchModels(brand);
  };

  const handleSelectModel = (model) => {
    setSelectedModel(model.name);
    fetchEngines(selectedBrand, model.name);
  };
  
  const handleSelectEngine = (engine) => {
    setSelectedEngine(engine);
    setShowEngineDetails(true);
  };

  // AI Populate all brands
  const handleAIPopulate = async () => {
    if (!confirm('KI-Katalogbefüllung für alle Marken starten? Dies kann einige Minuten dauern.')) return;
    setPopulating(true);
    try {
      const response = await axios.post(`${API}/vehicle-catalog/populate`, {}, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      toast.success(`Katalog aktualisiert: ${response.data.models_added || 0} Modelle, ${response.data.engines_added || 0} Motoren`);
      fetchBrands();
      fetchStats();
    } catch (error) {
      toast.error('Fehler bei der KI-Befüllung');
    } finally {
      setPopulating(false);
    }
  };

  // Populate single brand
  const handlePopulateBrand = async () => {
    if (!selectedBrand) return;
    if (!confirm(`KI-Datensammlung für ${selectedBrand} starten?`)) return;
    
    setPopulatingBrand(true);
    try {
      const response = await axios.post(
        `${API}/vehicle-catalog/brands/${encodeURIComponent(selectedBrand)}/populate`,
        {},
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      toast.success(`${response.data.models_added} Modelle und ${response.data.engines_added} Motoren für ${selectedBrand} hinzugefügt`);
      fetchModels(selectedBrand);
      fetchStats();
    } catch (error) {
      toast.error('Fehler bei der Datensammlung: ' + (error.response?.data?.detail || error.message));
    } finally {
      setPopulatingBrand(false);
    }
  };

  // Deep search for engines
  const handleDeepSearchEngines = async () => {
    if (!selectedBrand || !selectedModel || enhancingEngines) return;
    
    setEnhancingEngines(true);
    try {
      const response = await axios.post(
        `${API}/vehicle-catalog/brands/${encodeURIComponent(selectedBrand)}/models/${encodeURIComponent(selectedModel)}/enhance-engines`,
        {},
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      
      if (response.data.engines_added > 0) {
        toast.success(`${response.data.engines_added} neue Motoren gefunden!`);
        fetchEngines(selectedBrand, selectedModel);
        fetchStats();
      } else {
        toast.info('Keine zusätzlichen Motorvarianten gefunden.');
      }
    } catch (error) {
      toast.error('Fehler bei der Tiefensuche');
    } finally {
      setEnhancingEngines(false);
    }
  };

  // Add handlers
  const handleAddBrand = async () => {
    if (!newItem.trim()) return;
    try {
      await axios.post(`${API}/vehicle-catalog/brands`, { name: newItem.trim() },
        { headers: { Authorization: `Bearer ${getToken()}` } });
      toast.success(`Marke "${newItem}" hinzugefügt`);
      setShowAddBrand(false);
      setNewItem('');
      fetchBrands();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler');
    }
  };

  const handleAddModel = async () => {
    if (!newItem.trim() || !selectedBrand) return;
    try {
      await axios.post(`${API}/vehicle-catalog/models`, { brand: selectedBrand, name: newItem.trim() },
        { headers: { Authorization: `Bearer ${getToken()}` } });
      toast.success(`Modell "${newItem}" hinzugefügt`);
      setShowAddModel(false);
      setNewItem('');
      fetchModels(selectedBrand);
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler');
    }
  };

  const handleAddEngine = async () => {
    if (!newEngineData.name.trim() || !selectedBrand || !selectedModel) return;
    try {
      const data = {
        ...newEngineData,
        brand: selectedBrand,
        model: selectedModel,
        displacement_cc: newEngineData.displacement_cc ? parseInt(newEngineData.displacement_cc) : null,
        power_ps: newEngineData.power_ps ? parseInt(newEngineData.power_ps) : null,
        power_kw: newEngineData.power_kw ? parseInt(newEngineData.power_kw) : null,
        torque_nm: newEngineData.torque_nm ? parseInt(newEngineData.torque_nm) : null
      };
      
      await axios.post(`${API}/vehicle-catalog/engines`, data,
        { headers: { Authorization: `Bearer ${getToken()}` } });
      toast.success(`Motor "${newEngineData.name}" hinzugefügt`);
      setShowAddEngine(false);
      setNewEngineData({ name: '', engine_code: '', displacement_cc: '', fuel_type: 'petrol', power_ps: '', power_kw: '', torque_nm: '', aspiration: 'turbo', drivetrain: 'FWD' });
      fetchEngines(selectedBrand, selectedModel);
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler');
    }
  };

  // Delete handlers
  const handleDeleteBrand = async (brand) => {
    if (!confirm(`Marke "${brand}" und alle Daten löschen?`)) return;
    try {
      await axios.delete(`${API}/vehicle-catalog/brands/${encodeURIComponent(brand)}`,
        { headers: { Authorization: `Bearer ${getToken()}` } });
      toast.success(`Marke "${brand}" gelöscht`);
      if (selectedBrand === brand) { setSelectedBrand(null); setModels([]); setEngines([]); }
      fetchBrands();
      fetchStats();
    } catch (error) { toast.error('Fehler'); }
  };

  const handleDeleteModel = async (model) => {
    if (!confirm(`Modell "${model}" löschen?`)) return;
    try {
      await axios.delete(`${API}/vehicle-catalog/brands/${encodeURIComponent(selectedBrand)}/models/${encodeURIComponent(model)}`,
        { headers: { Authorization: `Bearer ${getToken()}` } });
      toast.success(`Modell "${model}" gelöscht`);
      if (selectedModel === model) { setSelectedModel(null); setEngines([]); }
      fetchModels(selectedBrand);
      fetchStats();
    } catch (error) { toast.error('Fehler'); }
  };

  const handleDeleteEngine = async (engine) => {
    if (!confirm(`Motor "${engine}" löschen?`)) return;
    try {
      await axios.delete(`${API}/vehicle-catalog/brands/${encodeURIComponent(selectedBrand)}/models/${encodeURIComponent(selectedModel)}/engines/${encodeURIComponent(engine)}`,
        { headers: { Authorization: `Bearer ${getToken()}` } });
      toast.success(`Motor gelöscht`);
      fetchEngines(selectedBrand, selectedModel);
      fetchStats();
    } catch (error) { toast.error('Fehler'); }
  };

  // Filtered lists
  const filteredBrands = useMemo(() => {
    if (!brandSearch) return brands;
    return brands.filter(b => b.toLowerCase().includes(brandSearch.toLowerCase()));
  }, [brands, brandSearch]);

  const filteredModels = useMemo(() => {
    if (!modelSearch) return models;
    return models.filter(m => m.name?.toLowerCase().includes(modelSearch.toLowerCase()));
  }, [models, modelSearch]);

  const filteredEngines = useMemo(() => {
    if (!engineSearch) return engines;
    const search = engineSearch.toLowerCase();
    return engines.filter(e => 
      e.name?.toLowerCase().includes(search) ||
      e.engine_code?.toLowerCase().includes(search) ||
      e.fuel_type?.toLowerCase().includes(search)
    );
  }, [engines, engineSearch]);

  // Get fuel type color
  const getFuelColor = (type) => {
    const colors = {
      petrol: '#22C55E',
      diesel: '#F59E0B',
      hybrid: '#3B82F6',
      electric: '#8B5CF6',
      plugin_hybrid: '#06B6D4'
    };
    return colors[type] || '#94A3B8';
  };

  return (
    <div className="min-h-screen p-8" style={{ backgroundColor: '#0B0F14' }}>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8 flex justify-between items-start">
          <div>
            <h1 className="text-3xl font-bold mb-2" style={{ color: '#E6EDF3' }}>
              Fahrzeugkatalog
            </h1>
            <p style={{ color: '#94A3B8' }}>
              Umfassende Fahrzeugdatenbank mit technischen Spezifikationen und Tuning-Daten
            </p>
          </div>
          <Button onClick={handleAIPopulate} disabled={populating} style={{ backgroundColor: '#22C55E', color: '#fff' }}>
            {populating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
            {populating ? 'Läuft...' : 'KI-Katalog aktualisieren'}
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-4 gap-6 mb-8">
          <Card style={{ backgroundColor: '#0F141A', borderColor: '#1F2937' }}>
            <CardContent className="p-4 flex items-center gap-4">
              <div className="p-3 rounded-lg" style={{ backgroundColor: '#1EA0E620' }}>
                <Database className="w-5 h-5" style={{ color: '#1EA0E6' }} />
              </div>
              <div>
                <p className="text-2xl font-bold" style={{ color: '#E6EDF3' }}>{stats.total_brands}</p>
                <p className="text-sm" style={{ color: '#94A3B8' }}>Marken</p>
              </div>
            </CardContent>
          </Card>
          <Card style={{ backgroundColor: '#0F141A', borderColor: '#1F2937' }}>
            <CardContent className="p-4 flex items-center gap-4">
              <div className="p-3 rounded-lg" style={{ backgroundColor: '#22C55E20' }}>
                <Car className="w-5 h-5" style={{ color: '#22C55E' }} />
              </div>
              <div>
                <p className="text-2xl font-bold" style={{ color: '#E6EDF3' }}>{stats.total_models}</p>
                <p className="text-sm" style={{ color: '#94A3B8' }}>Modelle</p>
              </div>
            </CardContent>
          </Card>
          <Card style={{ backgroundColor: '#0F141A', borderColor: '#1F2937' }}>
            <CardContent className="p-4 flex items-center gap-4">
              <div className="p-3 rounded-lg" style={{ backgroundColor: '#F59E0B20' }}>
                <Settings className="w-5 h-5" style={{ color: '#F59E0B' }} />
              </div>
              <div>
                <p className="text-2xl font-bold" style={{ color: '#E6EDF3' }}>{stats.total_engines}</p>
                <p className="text-sm" style={{ color: '#94A3B8' }}>Motoren</p>
              </div>
            </CardContent>
          </Card>
          <Card style={{ backgroundColor: '#0F141A', borderColor: '#1F2937' }}>
            <CardContent className="p-4 flex items-center gap-4">
              <div className="p-3 rounded-lg" style={{ backgroundColor: '#8B5CF620' }}>
                <Zap className="w-5 h-5" style={{ color: '#8B5CF6' }} />
              </div>
              <div>
                <p className="text-2xl font-bold" style={{ color: '#E6EDF3' }}>{stats.engines_with_tuning}</p>
                <p className="text-sm" style={{ color: '#94A3B8' }}>Mit Tuning</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Three Column Layout */}
        <div className="grid grid-cols-3 gap-6">
          {/* Brands Column */}
          <Card style={{ backgroundColor: '#0F141A', borderColor: '#1F2937' }}>
            <CardHeader className="pb-3">
              <div className="flex justify-between items-center">
                <CardTitle style={{ color: '#E6EDF3' }}>Marken</CardTitle>
                <Button size="sm" onClick={() => { setNewItem(''); setShowAddBrand(true); }} style={{ backgroundColor: '#1EA0E6', color: '#fff' }}>
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              <div className="relative mt-2">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: '#94A3B8' }} />
                <Input value={brandSearch} onChange={(e) => setBrandSearch(e.target.value)} placeholder="Marke suchen..." className="pl-9" style={{ backgroundColor: '#0B0F14', borderColor: '#1F2937', color: '#E6EDF3' }} />
              </div>
            </CardHeader>
            <CardContent className="max-h-[500px] overflow-y-auto">
              {loading ? (
                <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin" style={{ color: '#7CD4FD' }} /></div>
              ) : filteredBrands.length === 0 ? (
                <p className="text-center py-4" style={{ color: '#94A3B8' }}>Keine Marken</p>
              ) : (
                <div className="space-y-1">
                  {filteredBrands.map((brand) => (
                    <div key={brand} className="flex items-center justify-between p-2 rounded cursor-pointer group" style={{ backgroundColor: selectedBrand === brand ? '#1EA0E620' : 'transparent' }} onClick={() => handleSelectBrand(brand)}>
                      <span style={{ color: '#E6EDF3' }}>{brand}</span>
                      <div className="flex items-center gap-1">
                        <Button size="sm" variant="ghost" className="opacity-0 group-hover:opacity-100 h-6 w-6 p-0" onClick={(e) => { e.stopPropagation(); handleDeleteBrand(brand); }}>
                          <Trash2 className="w-3 h-3" style={{ color: '#EF4444' }} />
                        </Button>
                        <ChevronRight className="w-4 h-4" style={{ color: '#94A3B8' }} />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Models Column */}
          <Card style={{ backgroundColor: '#0F141A', borderColor: '#1F2937' }}>
            <CardHeader className="pb-3">
              <div className="flex justify-between items-center">
                <CardTitle style={{ color: '#E6EDF3' }}>
                  Modelle {selectedBrand && <span style={{ color: '#7CD4FD' }}>({selectedBrand})</span>}
                </CardTitle>
                <div className="flex gap-1">
                  <Button size="sm" onClick={handlePopulateBrand} disabled={!selectedBrand || populatingBrand} title="KI-Daten für diese Marke sammeln" style={{ backgroundColor: selectedBrand && !populatingBrand ? '#22C55E' : '#374151', color: '#fff' }}>
                    {populatingBrand ? <Loader2 className="w-4 h-4 animate-spin" /> : <TrendingUp className="w-4 h-4" />}
                  </Button>
                  <Button size="sm" onClick={() => { setNewItem(''); setShowAddModel(true); }} disabled={!selectedBrand} style={{ backgroundColor: selectedBrand ? '#1EA0E6' : '#374151', color: '#fff' }}>
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
              </div>
              <div className="relative mt-2">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: '#94A3B8' }} />
                <Input value={modelSearch} onChange={(e) => setModelSearch(e.target.value)} placeholder="Modell suchen..." className="pl-9" disabled={!selectedBrand} style={{ backgroundColor: '#0B0F14', borderColor: '#1F2937', color: '#E6EDF3' }} />
              </div>
            </CardHeader>
            <CardContent className="max-h-[500px] overflow-y-auto">
              {!selectedBrand ? (
                <p className="text-center py-8" style={{ color: '#94A3B8' }}>Marke wählen</p>
              ) : loadingModels ? (
                <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin" style={{ color: '#7CD4FD' }} /></div>
              ) : filteredModels.length === 0 ? (
                <p className="text-center py-4" style={{ color: '#94A3B8' }}>Keine Modelle</p>
              ) : (
                <div className="space-y-1">
                  {filteredModels.map((model) => (
                    <div key={model.name} className="flex items-center justify-between p-2 rounded cursor-pointer group" style={{ backgroundColor: selectedModel === model.name ? '#22C55E20' : 'transparent' }} onClick={() => handleSelectModel(model)}>
                      <div>
                        <span style={{ color: '#E6EDF3' }}>{model.name}</span>
                        {model.category && <span className="text-xs ml-2 px-1.5 py-0.5 rounded" style={{ backgroundColor: '#1F2937', color: '#94A3B8' }}>{model.category}</span>}
                        {model.years?.length > 0 && <span className="text-xs ml-1" style={{ color: '#94A3B8' }}>({model.years[0]}-{model.years[model.years.length - 1]})</span>}
                      </div>
                      <div className="flex items-center gap-1">
                        <span className="text-xs" style={{ color: '#94A3B8' }}>{model.engines?.length || 0}</span>
                        <Button size="sm" variant="ghost" className="opacity-0 group-hover:opacity-100 h-6 w-6 p-0" onClick={(e) => { e.stopPropagation(); handleDeleteModel(model.name); }}>
                          <Trash2 className="w-3 h-3" style={{ color: '#EF4444' }} />
                        </Button>
                        <ChevronRight className="w-4 h-4" style={{ color: '#94A3B8' }} />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Engines Column */}
          <Card style={{ backgroundColor: '#0F141A', borderColor: '#1F2937' }}>
            <CardHeader className="pb-3">
              <div className="flex justify-between items-center">
                <CardTitle style={{ color: '#E6EDF3' }}>
                  Motoren {selectedModel && <span style={{ color: '#F59E0B' }}>({selectedModel})</span>}
                </CardTitle>
                <div className="flex gap-1">
                  <Button size="sm" onClick={handleDeepSearchEngines} disabled={!selectedModel || enhancingEngines} title="KI-Tiefensuche für Motoren" style={{ backgroundColor: selectedModel && !enhancingEngines ? '#22C55E' : '#374151', color: '#fff' }}>
                    {enhancingEngines ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                  </Button>
                  <Button size="sm" onClick={() => { setNewEngineData({ name: '', engine_code: '', displacement_cc: '', fuel_type: 'petrol', power_ps: '', power_kw: '', torque_nm: '', aspiration: 'turbo', drivetrain: 'FWD' }); setShowAddEngine(true); }} disabled={!selectedModel} style={{ backgroundColor: selectedModel ? '#1EA0E6' : '#374151', color: '#fff' }}>
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
              </div>
              <div className="relative mt-2">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: '#94A3B8' }} />
                <Input value={engineSearch} onChange={(e) => setEngineSearch(e.target.value)} placeholder="Motor suchen..." className="pl-9" disabled={!selectedModel} style={{ backgroundColor: '#0B0F14', borderColor: '#1F2937', color: '#E6EDF3' }} />
              </div>
            </CardHeader>
            <CardContent className="max-h-[500px] overflow-y-auto">
              {!selectedModel ? (
                <p className="text-center py-8" style={{ color: '#94A3B8' }}>Modell wählen</p>
              ) : loadingEngines ? (
                <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin" style={{ color: '#7CD4FD' }} /></div>
              ) : filteredEngines.length === 0 ? (
                <p className="text-center py-4" style={{ color: '#94A3B8' }}>Keine Motoren</p>
              ) : (
                <div className="space-y-2">
                  {filteredEngines.map((engine, idx) => (
                    <div key={idx} className="p-3 rounded-lg cursor-pointer group hover:bg-opacity-80" style={{ backgroundColor: '#1F2937' }} onClick={() => handleSelectEngine(engine)}>
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className="font-medium" style={{ color: '#E6EDF3' }}>{engine.name}</span>
                            {engine.fuel_type && (
                              <span className="text-xs px-1.5 py-0.5 rounded" style={{ backgroundColor: getFuelColor(engine.fuel_type) + '30', color: getFuelColor(engine.fuel_type) }}>
                                {FUEL_LABELS[engine.fuel_type] || engine.fuel_type}
                              </span>
                            )}
                          </div>
                          <div className="flex gap-3 mt-1 text-xs" style={{ color: '#94A3B8' }}>
                            {engine.power_ps && <span className="flex items-center gap-1"><Gauge className="w-3 h-3" />{engine.power_ps} PS</span>}
                            {engine.torque_nm && <span>{engine.torque_nm} Nm</span>}
                            {engine.displacement_cc && <span>{(engine.displacement_cc / 1000).toFixed(1)}L</span>}
                            {engine.engine_code && <span style={{ color: '#7CD4FD' }}>{engine.engine_code}</span>}
                          </div>
                          {engine.tuning?.stage1_power_ps && (
                            <div className="flex items-center gap-1 mt-1 text-xs" style={{ color: '#8B5CF6' }}>
                              <Zap className="w-3 h-3" />
                              Stage 1: {engine.tuning.stage1_power_ps} PS (+{engine.tuning.stage1_power_ps - engine.power_ps})
                            </div>
                          )}
                        </div>
                        <Button size="sm" variant="ghost" className="opacity-0 group-hover:opacity-100 h-6 w-6 p-0" onClick={(e) => { e.stopPropagation(); handleDeleteEngine(engine.name); }}>
                          <Trash2 className="w-3 h-3" style={{ color: '#EF4444' }} />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Add Brand Dialog */}
        <Dialog open={showAddBrand} onOpenChange={setShowAddBrand}>
          <DialogContent style={{ backgroundColor: '#0F141A', borderColor: '#1F2937' }}>
            <DialogHeader><DialogTitle style={{ color: '#E6EDF3' }}>Neue Marke</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div>
                <Label style={{ color: '#E6EDF3' }}>Markenname</Label>
                <Input value={newItem} onChange={(e) => setNewItem(e.target.value)} placeholder="z.B. BMW, Mercedes" style={{ backgroundColor: '#0B0F14', borderColor: '#1F2937', color: '#E6EDF3' }} />
              </div>
              <Button onClick={handleAddBrand} className="w-full" style={{ backgroundColor: '#1EA0E6', color: '#fff' }}>Hinzufügen</Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Add Model Dialog */}
        <Dialog open={showAddModel} onOpenChange={setShowAddModel}>
          <DialogContent style={{ backgroundColor: '#0F141A', borderColor: '#1F2937' }}>
            <DialogHeader><DialogTitle style={{ color: '#E6EDF3' }}>Neues Modell für {selectedBrand}</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div>
                <Label style={{ color: '#E6EDF3' }}>Modellname</Label>
                <Input value={newItem} onChange={(e) => setNewItem(e.target.value)} placeholder="z.B. 3er, C-Klasse" style={{ backgroundColor: '#0B0F14', borderColor: '#1F2937', color: '#E6EDF3' }} />
              </div>
              <Button onClick={handleAddModel} className="w-full" style={{ backgroundColor: '#1EA0E6', color: '#fff' }}>Hinzufügen</Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Add Engine Dialog */}
        <Dialog open={showAddEngine} onOpenChange={setShowAddEngine}>
          <DialogContent style={{ backgroundColor: '#0F141A', borderColor: '#1F2937' }} className="max-w-2xl">
            <DialogHeader><DialogTitle style={{ color: '#E6EDF3' }}>Neuer Motor für {selectedBrand} {selectedModel}</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label style={{ color: '#E6EDF3' }}>Bezeichnung *</Label>
                <Input value={newEngineData.name} onChange={(e) => setNewEngineData({...newEngineData, name: e.target.value})} placeholder="z.B. 320d, 2.0 TDI 150 PS" style={{ backgroundColor: '#0B0F14', borderColor: '#1F2937', color: '#E6EDF3' }} />
              </div>
              <div>
                <Label style={{ color: '#E6EDF3' }}>Motorcode</Label>
                <Input value={newEngineData.engine_code} onChange={(e) => setNewEngineData({...newEngineData, engine_code: e.target.value})} placeholder="z.B. B47D20, EA888" style={{ backgroundColor: '#0B0F14', borderColor: '#1F2937', color: '#E6EDF3' }} />
              </div>
              <div>
                <Label style={{ color: '#E6EDF3' }}>Hubraum (ccm)</Label>
                <Input type="number" value={newEngineData.displacement_cc} onChange={(e) => setNewEngineData({...newEngineData, displacement_cc: e.target.value})} placeholder="z.B. 1998" style={{ backgroundColor: '#0B0F14', borderColor: '#1F2937', color: '#E6EDF3' }} />
              </div>
              <div>
                <Label style={{ color: '#E6EDF3' }}>Kraftstoff</Label>
                <select value={newEngineData.fuel_type} onChange={(e) => setNewEngineData({...newEngineData, fuel_type: e.target.value})} className="w-full h-10 rounded-md border px-3" style={{ backgroundColor: '#0B0F14', borderColor: '#1F2937', color: '#E6EDF3' }}>
                  <option value="petrol">Benzin</option>
                  <option value="diesel">Diesel</option>
                  <option value="hybrid">Hybrid</option>
                  <option value="electric">Elektro</option>
                </select>
              </div>
              <div>
                <Label style={{ color: '#E6EDF3' }}>Leistung (PS)</Label>
                <Input type="number" value={newEngineData.power_ps} onChange={(e) => setNewEngineData({...newEngineData, power_ps: e.target.value})} placeholder="z.B. 190" style={{ backgroundColor: '#0B0F14', borderColor: '#1F2937', color: '#E6EDF3' }} />
              </div>
              <div>
                <Label style={{ color: '#E6EDF3' }}>Drehmoment (Nm)</Label>
                <Input type="number" value={newEngineData.torque_nm} onChange={(e) => setNewEngineData({...newEngineData, torque_nm: e.target.value})} placeholder="z.B. 400" style={{ backgroundColor: '#0B0F14', borderColor: '#1F2937', color: '#E6EDF3' }} />
              </div>
              <div>
                <Label style={{ color: '#E6EDF3' }}>Aufladung</Label>
                <select value={newEngineData.aspiration} onChange={(e) => setNewEngineData({...newEngineData, aspiration: e.target.value})} className="w-full h-10 rounded-md border px-3" style={{ backgroundColor: '#0B0F14', borderColor: '#1F2937', color: '#E6EDF3' }}>
                  <option value="turbo">Turbo</option>
                  <option value="supercharged">Kompressor</option>
                  <option value="naturally_aspirated">Sauger</option>
                  <option value="electric">Elektrisch</option>
                </select>
              </div>
              <div>
                <Label style={{ color: '#E6EDF3' }}>Antrieb</Label>
                <select value={newEngineData.drivetrain} onChange={(e) => setNewEngineData({...newEngineData, drivetrain: e.target.value})} className="w-full h-10 rounded-md border px-3" style={{ backgroundColor: '#0B0F14', borderColor: '#1F2937', color: '#E6EDF3' }}>
                  <option value="FWD">Frontantrieb</option>
                  <option value="RWD">Hinterradantrieb</option>
                  <option value="AWD">Allrad</option>
                </select>
              </div>
            </div>
            <Button onClick={handleAddEngine} className="w-full mt-4" style={{ backgroundColor: '#1EA0E6', color: '#fff' }}>Motor hinzufügen</Button>
          </DialogContent>
        </Dialog>

        {/* Engine Details Dialog */}
        <Dialog open={showEngineDetails} onOpenChange={setShowEngineDetails}>
          <DialogContent style={{ backgroundColor: '#0F141A', borderColor: '#1F2937' }} className="max-w-2xl">
            <DialogHeader>
              <DialogTitle style={{ color: '#E6EDF3' }}>
                {selectedEngine?.name}
              </DialogTitle>
            </DialogHeader>
            {selectedEngine && (
              <div className="space-y-6">
                {/* Technical Specs */}
                <div>
                  <h4 className="font-semibold mb-3" style={{ color: '#7CD4FD' }}>Technische Daten</h4>
                  <div className="grid grid-cols-2 gap-3">
                    {selectedEngine.engine_code && <div className="p-3 rounded-lg" style={{ backgroundColor: '#1F2937' }}><span className="text-xs" style={{ color: '#94A3B8' }}>Motorcode</span><p style={{ color: '#E6EDF3' }}>{selectedEngine.engine_code}</p></div>}
                    {selectedEngine.displacement_cc && <div className="p-3 rounded-lg" style={{ backgroundColor: '#1F2937' }}><span className="text-xs" style={{ color: '#94A3B8' }}>Hubraum</span><p style={{ color: '#E6EDF3' }}>{selectedEngine.displacement_cc} ccm</p></div>}
                    {selectedEngine.fuel_type && <div className="p-3 rounded-lg" style={{ backgroundColor: '#1F2937' }}><span className="text-xs" style={{ color: '#94A3B8' }}>Kraftstoff</span><p style={{ color: '#E6EDF3' }}>{FUEL_LABELS[selectedEngine.fuel_type] || selectedEngine.fuel_type}</p></div>}
                    {selectedEngine.aspiration && <div className="p-3 rounded-lg" style={{ backgroundColor: '#1F2937' }}><span className="text-xs" style={{ color: '#94A3B8' }}>Aufladung</span><p style={{ color: '#E6EDF3' }}>{ASPIRATION_LABELS[selectedEngine.aspiration] || selectedEngine.aspiration}</p></div>}
                    {selectedEngine.drivetrain && <div className="p-3 rounded-lg" style={{ backgroundColor: '#1F2937' }}><span className="text-xs" style={{ color: '#94A3B8' }}>Antrieb</span><p style={{ color: '#E6EDF3' }}>{selectedEngine.drivetrain}</p></div>}
                  </div>
                </div>
                
                {/* Performance */}
                <div>
                  <h4 className="font-semibold mb-3" style={{ color: '#22C55E' }}>Leistung Original</h4>
                  <div className="grid grid-cols-2 gap-3">
                    {selectedEngine.power_ps && <div className="p-3 rounded-lg" style={{ backgroundColor: '#1F2937' }}><span className="text-xs" style={{ color: '#94A3B8' }}>Leistung</span><p className="text-xl font-bold" style={{ color: '#22C55E' }}>{selectedEngine.power_ps} PS <span className="text-sm font-normal" style={{ color: '#94A3B8' }}>({selectedEngine.power_kw || Math.round(selectedEngine.power_ps / 1.36)} kW)</span></p></div>}
                    {selectedEngine.torque_nm && <div className="p-3 rounded-lg" style={{ backgroundColor: '#1F2937' }}><span className="text-xs" style={{ color: '#94A3B8' }}>Drehmoment</span><p className="text-xl font-bold" style={{ color: '#F59E0B' }}>{selectedEngine.torque_nm} Nm</p></div>}
                  </div>
                </div>
                
                {/* Tuning */}
                {selectedEngine.tuning && (selectedEngine.tuning.stage1_power_ps || selectedEngine.tuning.stage2_power_ps) && (
                  <div>
                    <h4 className="font-semibold mb-3 flex items-center gap-2" style={{ color: '#8B5CF6' }}>
                      <Zap className="w-4 h-4" /> Tuning-Potenzial
                    </h4>
                    <div className="space-y-2">
                      {selectedEngine.tuning.stage1_power_ps && (
                        <div className="p-3 rounded-lg flex justify-between items-center" style={{ backgroundColor: '#8B5CF620' }}>
                          <span style={{ color: '#E6EDF3' }}>Stage 1 (ECU)</span>
                          <div className="text-right">
                            <span className="font-bold" style={{ color: '#8B5CF6' }}>{selectedEngine.tuning.stage1_power_ps} PS</span>
                            <span className="text-xs ml-2" style={{ color: '#22C55E' }}>+{selectedEngine.tuning.stage1_power_ps - selectedEngine.power_ps} PS</span>
                            {selectedEngine.tuning.stage1_torque_nm && <span className="text-xs ml-2" style={{ color: '#F59E0B' }}>{selectedEngine.tuning.stage1_torque_nm} Nm</span>}
                          </div>
                        </div>
                      )}
                      {selectedEngine.tuning.stage2_power_ps && (
                        <div className="p-3 rounded-lg flex justify-between items-center" style={{ backgroundColor: '#8B5CF630' }}>
                          <span style={{ color: '#E6EDF3' }}>Stage 2 (Hardware)</span>
                          <div className="text-right">
                            <span className="font-bold" style={{ color: '#8B5CF6' }}>{selectedEngine.tuning.stage2_power_ps} PS</span>
                            <span className="text-xs ml-2" style={{ color: '#22C55E' }}>+{selectedEngine.tuning.stage2_power_ps - selectedEngine.power_ps} PS</span>
                            {selectedEngine.tuning.stage2_torque_nm && <span className="text-xs ml-2" style={{ color: '#F59E0B' }}>{selectedEngine.tuning.stage2_torque_nm} Nm</span>}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};
